import { environment } from '../../environments/environment';

export interface PhoneSupportRuntimeConfig {
  patientAuthenticationServiceUrl?: string;
  caregiverAuthenticationServiceUrl?: string;
  thriveCloudFunctionsUrl?: string;
  engageCloudFunctionsUrl?: string;
  cloudCoreApiUrl?: string;
  patientDataServiceUrl?: string;
  appleServiceId?: string;
  recaptchaSiteKey?: string;
  appName?: string;
  googleAnalyticsMeasurementId?: string;
}

declare global {
  interface Window {
    __PHONE_SUPPORT_CONFIG__?: PhoneSupportRuntimeConfig;
  }
}

function runtimeUrl(
  key: keyof Omit<
    PhoneSupportRuntimeConfig,
    'appName' | 'googleAnalyticsMeasurementId' | 'recaptchaSiteKey'
  >,
  fallback: string
): string {
  const configured = window.__PHONE_SUPPORT_CONFIG__?.[key];
  if (!configured) return fallback.replace(/\/+$/, '');

  try {
    const url = new URL(configured);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      throw new Error(`Unsupported URL protocol: ${url.protocol}`);
    }
    return configured.replace(/\/+$/, '');
  } catch {
    console.warn(`Ignoring invalid Phone Support runtime setting "${key}".`);
    return fallback.replace(/\/+$/, '');
  }
}

export const runtimeConfig = {
  get patientAuthenticationServiceUrl(): string {
    return runtimeUrl(
      'patientAuthenticationServiceUrl',
      environment.patientAuthenticationServiceUrl
    );
  },
  get caregiverAuthenticationServiceUrl(): string {
    return runtimeUrl(
      'caregiverAuthenticationServiceUrl',
      environment.caregiverAuthenticationServiceUrl
    );
  },
  get thriveCloudFunctionsUrl(): string {
    return runtimeUrl('thriveCloudFunctionsUrl', environment.thriveCloudFunctionsUrl);
  },
  get engageCloudFunctionsUrl(): string {
    return runtimeUrl('engageCloudFunctionsUrl', environment.engageCloudFunctionsUrl);
  },
  get cloudCoreApiUrl(): string {
    return runtimeUrl('cloudCoreApiUrl', environment.cloudCoreApiUrl);
  },
  get appName(): string {
    return window.__PHONE_SUPPORT_CONFIG__?.appName?.trim() || environment.appName;
  },
  get googleAnalyticsMeasurementId(): string {
    const configured = window.__PHONE_SUPPORT_CONFIG__?.googleAnalyticsMeasurementId;
    return configured === undefined ? 'G-KKYDL6VSJ1' : configured.trim();
  },
  get patientDataServiceUrl(): string {
    return runtimeUrl('patientDataServiceUrl', environment.patientDataServiceUrl);
  },
  get appleServiceId(): string {
    return window.__PHONE_SUPPORT_CONFIG__?.appleServiceId?.trim() || environment.appleServiceId;
  },
  appleServiceIdFor(applicationName: string): string {
    switch (applicationName.toLowerCase()) {
      case 'hearcircle': return 'com.starkey.hearcircle.serviceid';
      case 'myaudibel': return 'com.starkey.audibel.serviceid';
      default: return this.appleServiceId;
    }
  },
  get recaptchaSiteKey(): string {
    return window.__PHONE_SUPPORT_CONFIG__?.recaptchaSiteKey?.trim() || environment.recaptchaSiteKey;
  }
};

